import api from './api';
import {
  Session,
  SessionDetail,
  CreateSessionRequest,
  SessionStudentInfo,
  AddStudentInput,
  StudentCredentials,
} from '../types/session.types';
import { StudentSession } from '../types/progress.types';

export const sessionService = {
  async getSessions(params?: {
    status?: string;
    workshopId?: string;
  }): Promise<Session[]> {
    const response = await api.get('/sessions', { params });
    return response.data;
  },

  async getSession(id: string): Promise<SessionDetail> {
    const response = await api.get(`/sessions/${id}`);
    return response.data;
  },

  async createSession(data: CreateSessionRequest): Promise<SessionDetail> {
    const response = await api.post('/sessions', data);
    return response.data;
  },

  async updateSession(
    id: string,
    data: Partial<CreateSessionRequest>,
  ): Promise<SessionDetail> {
    const response = await api.patch(`/sessions/${id}`, data);
    return response.data;
  },

  async prepareSession(id: string): Promise<SessionDetail> {
    const response = await api.post(`/sessions/${id}/prepare`);
    return response.data;
  },

  async getDesktopStatus(sessionId: string): Promise<any[]> {
    const response = await api.get(`/kiosk/sessions/${sessionId}/desktops`);
    return response.data;
  },

  async cleanupDesktops(sessionId: string): Promise<void> {
    await api.delete(`/kiosk/sessions/${sessionId}/desktops`);
  },

  async startSession(id: string): Promise<SessionDetail> {
    const response = await api.post(`/sessions/${id}/start`);
    return response.data;
  },

  async pauseSession(id: string): Promise<SessionDetail> {
    const response = await api.post(`/sessions/${id}/pause`);
    return response.data;
  },

  async endSession(id: string): Promise<SessionDetail> {
    const response = await api.post(`/sessions/${id}/end`);
    return response.data;
  },

  async deleteSession(id: string): Promise<void> {
    await api.delete(`/sessions/${id}`);
  },

  async getMySessionsAsStudent(): Promise<StudentSession[]> {
    const response = await api.get('/sessions/my-sessions');
    return response.data;
  },

  // Student roster
  async getStudents(sessionId: string): Promise<SessionStudentInfo[]> {
    const response = await api.get(`/sessions/${sessionId}/students`);
    return response.data;
  },

  async addStudents(
    sessionId: string,
    students: AddStudentInput[],
  ): Promise<{ added: SessionStudentInfo[]; alreadyEnrolled: string[] }> {
    const response = await api.post(`/sessions/${sessionId}/students`, {
      students,
    });
    return response.data;
  },

  async updateStudentCredentials(
    sessionId: string,
    studentId: string,
    data: {
      username?: string;
      tempPassword?: string;
      tenantUrl?: string;
      tenantAdminEmail?: string;
      tenantAdminPassword?: string;
      sharingPortalUrl?: string;
      skytapPortalPassword?: string;
    },
  ): Promise<SessionStudentInfo> {
    const response = await api.patch(
      `/sessions/${sessionId}/students/${studentId}`,
      data,
    );
    return response.data;
  },

  async sendInvitations(sessionId: string): Promise<{
    sent: string[];
    failed: { email: string; error: string }[];
  }> {
    const response = await api.post(`/sessions/${sessionId}/send-invitations`);
    return response.data;
  },

  async removeStudent(sessionId: string, studentId: string): Promise<void> {
    await api.delete(`/sessions/${sessionId}/students/${studentId}`);
  },

  async getMyCredentials(sessionId: string): Promise<StudentCredentials> {
    const response = await api.get(`/sessions/${sessionId}/my-credentials`);
    return response.data;
  },

  // Co-instructor management
  async getInstructors(): Promise<Array<{ id: string; firstName: string; lastName: string; email: string }>> {
    const response = await api.get('/users/instructors');
    return response.data;
  },

  async addCoInstructors(sessionId: string, instructorIds: string[]): Promise<any> {
    const response = await api.post(`/sessions/${sessionId}/instructors`, { instructorIds });
    return response.data;
  },

  async getCoInstructors(sessionId: string): Promise<any[]> {
    const response = await api.get(`/sessions/${sessionId}/instructors`);
    return response.data;
  },

  async removeCoInstructor(sessionId: string, instructorId: string): Promise<void> {
    await api.delete(`/sessions/${sessionId}/instructors/${instructorId}`);
  },
};
